/**
 * JournalRepository — one entry per (user, date), enforced by a unique
 * constraint in SQL; `save` upserts on that conflict target.
 */
import { requireSupabase } from '@/lib/supabase/client'
import {
  journalEntryFromRow,
  journalEntryToInsert,
  type JournalEntryRow,
} from '@/lib/supabase/mappers'
import type { JournalEntry } from '@/types'
import type { JournalRepository } from '../interfaces'
import { assertRowReturned, fail, requireUserId } from './helpers'

export const journalRepository: JournalRepository = {
  async list(limit = 30) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(limit)
    if (error) fail(error)
    return ((data ?? []) as JournalEntryRow[]).map(journalEntryFromRow)
  },

  async getByDate(date): Promise<JournalEntry | null> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
      .maybeSingle()
    if (error) fail(error)
    return data ? journalEntryFromRow(data as JournalEntryRow) : null
  },

  async save(entry) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('journal_entries')
      .upsert(journalEntryToInsert(entry, userId), { onConflict: 'user_id,date' })
      .select()
      .single()
    return journalEntryFromRow(assertRowReturned(data, error) as JournalEntryRow)
  },

  async remove(id) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { error } = await supabase.from('journal_entries').delete().eq('id', id).eq('user_id', userId)
    if (error) fail(error)
  },
}
